import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
  StyleSheet,
  PixelRatio,
  Dimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import Colours from "../../utils/Colours";
import AppContext from "../../context/AppContext";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import axios from "../../api/axios";
import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";
import ErrorAlertComp from "../../alertComp/ErrorAlertComp";

const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};
const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

// ─── MOCK BILL DATA (replace with real backend call later) ───────────────────
const MOCK_BILL = {
  hasBill: true, // flip to false to see the "no bill" state
  ordinaryAmount: 187500,
  tableName: "Olu's Table",
};
// ─────────────────────────────────────────────────────────────────────────────

const DOINGS_CAP = 50000;
const DOINGS_RATE = 0.08;

const computeDoingsAmount = (ordinary) => {
  const premium = Math.min(ordinary * DOINGS_RATE, DOINGS_CAP);
  return ordinary + premium;
};

const WaitForBillsPage = () => {
  const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading2, setIsLoading2] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null); // "ordinary" | "doings" | null
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  // Bill state (will come from backend later)
  const [bill, setBill] = useState(null); // null = not fetched yet
  const [billChecked, setBillChecked] = useState(false); // true once we've checked at least once

  const navigation = useNavigation();
  const route = useRoute();
  const { userToken } = useContext(AppContext);
  const axiosPrivate = useAxiosPrivate();
  const isLoggedIn = !!userToken;

  const qrToken = route?.params?.qrToken;
  const tableId = route?.params?.tableId;

  /* ── QR verification ────────────────────────────────────────────────────── */
  const fetchQRDetails = async (token) => {
    if (!token) return null;
    const response = await axios.get(`/getVerifyQR/getVerifyQR/${token}`);
    return response.data;
  };

  const { data: qrLocation, isLoading: isLoadingQR } = useQuery({
    queryKey: ["verifyQR", qrToken],
    queryFn: () => fetchQRDetails(qrToken),
    enabled: !!qrToken,
    staleTime: 1000 * 60 * 60,
  });

  const brandID = qrLocation?.brandID;
  const waiterID = qrLocation?.waiterID;
  const tableName = qrLocation?.tableName || MOCK_BILL.tableName;

  /* ── Redirect guard ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (isLoadingQR) return;
    if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
      userToken ? navigation.replace("main") : navigation.navigate("getStarted");
    }
  }, [brandID, waiterID, isLoadingQR]);

  /* ── Bill helpers ───────────────────────────────────────────────────────── */
  const ordinaryAmount = bill?.ordinaryAmount ?? 0;
  const doingsAmount = computeDoingsAmount(ordinaryAmount);
  const doingsPremium = doingsAmount - ordinaryAmount;

  const hasBill = billChecked && bill?.hasBill;

  /* ── Check bill (mock — swap for real API call) ─────────────────────────── */
  const handleCheckIfBillArrived = async () => {
    try {
      setIsLoading2(true);

      // ── Replace this block with your real API call ──────────────────────
      // const response = await axiosPrivate.get(`/checkBill/checkBill/${tableId}`);
      // setBill(response.data);
      await new Promise((res) => setTimeout(res, 800)); // simulate network
      setBill(MOCK_BILL);
      // ────────────────────────────────────────────────────────────────────

      setBillChecked(true);
      setSelectedAmount(null);
      setIsCustomSelected(false);
      setCustomAmount("");
    } catch (error) {
      console.error("Bill check failed", error);
      setErrorMessage("Could not fetch your bill. Please try again.");
      setIsErrorCompVisible(true);
    } finally {
      setIsLoading2(false);
    }
  };

  /* ── canProceed ─────────────────────────────────────────────────────────── */
  const canProceed = useMemo(() => {
    if (hasBill) {
      // Bill mode: user must pick ordinary or doings
      if (!selectedAmount) return false;
      return isLoggedIn ? true : whatsappNumber?.length > 10;
    } else {
      // No-bill mode: custom spend
      if (!isCustomSelected) return false;
      const parsed = Number(customAmount.replace(/[^0-9]/g, ""));
      const isAmountValid = parsed > 0;
      return isLoggedIn ? isAmountValid : isAmountValid && whatsappNumber?.length > 10;
    }
  }, [hasBill, selectedAmount, isCustomSelected, customAmount, isLoggedIn, whatsappNumber]);

  /* ── Proceed to payment ─────────────────────────────────────────────────── */
  const handleProceed = async () => {
    let finalPhoneNumber;

    if (!isLoggedIn) {
      let cleaned = whatsappNumber.replace(/\D/g, "").replace(/^0+/, "");
      if (cleaned.startsWith("234")) cleaned = cleaned.slice(3);
      cleaned = cleaned.replace(/^0+/, "");

      if (cleaned.length !== 10) {
        setErrorMessage("You might have entered a wrong number");
        setIsErrorCompVisible(true);
        return;
      }
      finalPhoneNumber = `234${cleaned}`;
    }

    const chosenAmount = hasBill
      ? selectedAmount === "doings"
        ? doingsAmount
        : ordinaryAmount
      : Number(customAmount.replace(/[^0-9]/g, ""));

    const payload = {
      phoneNumber: finalPhoneNumber,
      spendTierID: selectedAmount,
      customSpend: chosenAmount,
      tableAnonymity: isAnonymous,
      associatedBrand: brandID,
      waiterID,
      billType: selectedAmount, // "ordinary" | "doings" | null
    };

    try {
      setIsLoading2(true);
      if (isLoggedIn) {
        const response = await axiosPrivate.post("/createVirtualTable/createVirtualTable", payload);
        if (response.data.success) {
          navigation.navigate("payForTable", { tableId: response.data.tableData._id });
        }
      } else {
        const response = await axios.post("/requestOtp/requestOtp", payload);
        if (response.status === 200) {
          navigation.navigate("anonymousOnboardingOTP", { whatsappNumber: payload.phoneNumber });
        }
      }
    } catch (error) {
      console.error("Something went wrong", error);
      setErrorMessage("Something went wrong. Please try again");
      setIsErrorCompVisible(true);
    } finally {
      setIsLoading2(false);
    }
  };

  const handleBackNavigation = () => {
    Platform.OS === "web" && !isLoggedIn
      ? navigation.navigate("getStarted")
      : navigation.navigate("main");
  };

  /* ── Loading / guard states ─────────────────────────────────────────────── */
  if (isLoadingQR) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={Colours.PRIMARY} />
        <Text>Loading...</Text>
      </View>
    );
  }

  if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
    return null;
  }

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <SafeAreaView style={styles.safeArea}>
      <TopHeaderComponent headerText={tableName} hideScreen={handleBackNavigation} />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={{
            padding: dynamicSpacing(20),
            paddingBottom: dynamicSpacing(150),
            backgroundColor: Colours.LIGHTEST,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* ── NO BILL YET ───────────────────────────────────────────────── */}
          {!hasBill && (
            <View style={styles.noBillContainer}>
              <MaterialCommunityIcons
                name="receipt-text-clock-outline"
                size={dynamicSize(56)}
                color="#CCC"
              />
              <Text style={styles.noBillTitle}>Wait for your bill</Text>
              <Text style={styles.noBillSubtitle}>
                Your bill will show up here when it is ready. Tap the button below to check.
              </Text>
            </View>
          )}

          {/* ── BILL AVAILABLE: ordinary + doings cards ───────────────────── */}
          {hasBill && (
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>YOUR BILL</Text>

              {/* Ordinary Bill Card */}
              <TouchableOpacity
                style={[styles.billCard, selectedAmount === "ordinary" && styles.billCardActive]}
                onPress={() => setSelectedAmount("ordinary")}
                activeOpacity={0.85}
              >
                <View style={styles.billCardHeader}>
                  <MaterialCommunityIcons
                    name="receipt"
                    size={dynamicSize(22)}
                    color={selectedAmount === "ordinary" ? "white" : Colours.PRIMARY}
                  />
                  <Text
                    style={[
                      styles.billCardTitle,
                      selectedAmount === "ordinary" && { color: "white" },
                    ]}
                  >
                    Ordinary Bill
                  </Text>
                  {selectedAmount === "ordinary" && (
                    <MaterialCommunityIcons
                      name="check-circle"
                      size={dynamicSize(20)}
                      color="white"
                      style={{ marginLeft: "auto" }}
                    />
                  )}
                </View>
                <Text
                  style={[
                    styles.billCardAmount,
                    selectedAmount === "ordinary" && { color: "white" },
                  ]}
                >
                  ₦{ordinaryAmount.toLocaleString()}
                </Text>
                <Text
                  style={[
                    styles.billCardSub,
                    selectedAmount === "ordinary" && { color: "rgba(255,255,255,0.75)" },
                  ]}
                >
                  Standard table bill — pay exactly what you ordered.
                </Text>
              </TouchableOpacity>

              {/* Doings Bill Card */}
              <TouchableOpacity
                style={[styles.doingsCard, selectedAmount === "doings" && styles.doingsCardActive]}
                onPress={() => setSelectedAmount("doings")}
                activeOpacity={0.85}
              >
                {/* Glow badge */}
                <View style={styles.doingsBadge}>
                  <MaterialCommunityIcons
                    name="lightning-bolt"
                    size={dynamicSize(12)}
                    color="#fff"
                  />
                  <Text style={styles.doingsBadgeText}>DOINGS</Text>
                </View>

                <View style={styles.billCardHeader}>
                  <MaterialCommunityIcons name="crown" size={dynamicSize(22)} color="#FFD700" />
                  <Text
                    style={[
                      styles.billCardTitle,
                      { color: selectedAmount === "doings" ? "#FFD700" : "#FFD700" },
                    ]}
                  >
                    Doings Bill
                  </Text>
                  {selectedAmount === "doings" && (
                    <MaterialCommunityIcons
                      name="check-circle"
                      size={dynamicSize(20)}
                      color="#FFD700"
                      style={{ marginLeft: "auto" }}
                    />
                  )}
                </View>

                <View style={styles.doingsAmountRow}>
                  <Text style={styles.doingsAmount}>₦{doingsAmount.toLocaleString()}</Text>
                  <View style={styles.doingsPremiumBadge}>
                    <Text style={styles.doingsPremiumText}>
                      +₦{doingsPremium.toLocaleString()} premium
                    </Text>
                  </View>
                </View>

                <View style={styles.doingsPerks}>
                  {[
                    {
                      icon: "fire",
                      text: `Your table shows up on the Doings Trend as one of the highest spending tables at ${qrLocation?.brandName || "this venue"} regardless of amount spent`,
                    },
                    {
                      icon: "eye",
                      text: `Gain visibility at ${qrLocation?.brandName || "this venue"} and across the Meeza platform — just for hanging out`,
                    },
                    {
                      icon: "incognito",
                      text: "You can choose to stay anonymous and your table still stands out on the trend",
                    },
                  ].map((perk, i) => (
                    <View key={i} style={styles.doingsPerkRow}>
                      <MaterialCommunityIcons
                        name={perk.icon}
                        size={dynamicSize(16)}
                        color="#FFD700"
                        style={{ marginTop: 2 }}
                      />
                      <Text style={styles.doingsPerkText}>{perk.text}</Text>
                    </View>
                  ))}
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* ── CUSTOM SPEND (only when no bill yet) ──────────────────────── */}
          {!hasBill && (
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>OR ENTER A CUSTOM SPEND</Text>
              <View style={styles.inputSection}>
                <View
                  style={[styles.premiumInputContainer, isCustomSelected && styles.inputFocused]}
                >
                  <Text style={styles.inputCurrency}>₦</Text>
                  <TextInput
                    style={styles.fieldInput}
                    placeholder="Enter amount"
                    placeholderTextColor="#A3A3A3"
                    keyboardType="numeric"
                    value={customAmount}
                    onChangeText={(text) => {
                      const cleaned = text.replace(/[^0-9]/g, "");
                      setCustomAmount(cleaned);
                      setIsCustomSelected(cleaned !== "");
                    }}
                    onFocus={() => setIsCustomSelected(true)}
                  />
                </View>
              </View>
            </View>
          )}

          {/* ── CHECK BILL BUTTON ─────────────────────────────────────────── */}
          <TouchableOpacity
            style={styles.refreshBillBtn}
            onPress={handleCheckIfBillArrived}
            disabled={isLoading2}
            activeOpacity={0.8}
          >
            {isLoading2 ? (
              <ActivityIndicator size="small" color="#1A1A1A" />
            ) : (
              <>
                <MaterialCommunityIcons name="refresh" size={dynamicSize(18)} color="#1A1A1A" />
                <Text style={styles.refreshBillBtnText}>
                  {hasBill ? "Refresh Bill" : "Check if Bill Arrived"}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {/* ── PROCEED BUTTON ────────────────────────────────────────────── */}
          <TouchableOpacity
            style={[
              styles.proceedBtn,
              !canProceed && styles.disabledBtn,
              { marginTop: dynamicSpacing(12) },
            ]}
            onPress={handleProceed}
            disabled={!canProceed || isLoading2}
          >
            {isLoading2 ? (
              <ActivityIndicator color="white" />
            ) : (
              <>
                <Text style={styles.proceedBtnText}>
                  {isLoggedIn ? "Proceed to Payment" : "Get OTP to Confirm"}
                </Text>
                <MaterialCommunityIcons
                  name={isLoggedIn ? "credit-card-outline" : "arrow-right"}
                  size={dynamicSize(20)}
                  color="white"
                />
              </>
            )}
          </TouchableOpacity>

          <Text style={styles.footerNote}>
            Paystack secure payment follows {isLoggedIn ? "checkout" : "verification"}.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal>
    </SafeAreaView>
  );
};

export default WaitForBillsPage;

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: "white" },
  section: { marginBottom: dynamicSpacing(25) },
  sectionLabel: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#888",
    letterSpacing: 1.2,
    marginBottom: dynamicSpacing(12),
  },

  /* ── No bill state ── */
  noBillContainer: {
    alignItems: "center",
    paddingVertical: dynamicSpacing(40),
    marginBottom: dynamicSpacing(20),
  },
  noBillTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(22),
    color: "#1A1A1A",
    marginTop: dynamicSpacing(16),
    marginBottom: dynamicSpacing(8),
  },
  noBillSubtitle: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(13),
    color: "#999",
    textAlign: "center",
    lineHeight: dynamicFontSize(20),
    paddingHorizontal: dynamicSpacing(20),
  },

  /* ── Shared bill card base ── */
  billCard: {
    backgroundColor: "white",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(18),
    marginBottom: dynamicSpacing(12),
    borderWidth: 1,
    borderColor: "#EEE",
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  billCardActive: {
    backgroundColor: Colours.PRIMARY,
    borderColor: Colours.PRIMARY,
  },
  billCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(8),
    gap: dynamicSpacing(8),
  },
  billCardTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(16),
    color: "#1A1A1A",
  },
  billCardAmount: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(24),
    color: Colours.PRIMARY,
    marginBottom: dynamicSpacing(4),
  },
  billCardSub: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "#888",
  },

  /* ── Doings card ── */
  doingsCard: {
    backgroundColor: "#0D0D0D",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(18),
    marginBottom: dynamicSpacing(12),
    borderWidth: 1.5,
    borderColor: "#FFD700",
    elevation: 6,
    shadowColor: "#FFD700",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    overflow: "visible",
  },
  doingsCardActive: {
    borderColor: "#FFD700",
    backgroundColor: "#1A1400",
  },
  doingsBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: "#FFD700",
    borderRadius: dynamicSize(20),
    paddingHorizontal: dynamicSpacing(10),
    paddingVertical: dynamicSpacing(3),
    marginBottom: dynamicSpacing(12),
    gap: dynamicSpacing(4),
  },
  doingsBadgeText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(10),
    color: "#0D0D0D",
    letterSpacing: 1.2,
  },
  doingsAmountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(10),
    marginBottom: dynamicSpacing(14),
    flexWrap: "wrap",
  },
  doingsAmount: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(26),
    color: "#FFD700",
  },
  doingsPremiumBadge: {
    backgroundColor: "rgba(255,215,0,0.12)",
    borderRadius: dynamicSize(8),
    paddingHorizontal: dynamicSpacing(8),
    paddingVertical: dynamicSpacing(3),
    borderWidth: 1,
    borderColor: "rgba(255,215,0,0.3)",
  },
  doingsPremiumText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(11),
    color: "#FFD700",
  },
  doingsPerks: {
    gap: dynamicSpacing(10),
  },
  doingsPerkRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: dynamicSpacing(8),
  },
  doingsPerkText: {
    flex: 1,
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "rgba(255,255,255,0.8)",
    lineHeight: dynamicFontSize(18),
  },

  /* ── Custom input ── */
  inputSection: { paddingHorizontal: dynamicSpacing(4) },
  premiumInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FAFAFA",
    borderWidth: dynamicSize(1.5),
    borderColor: "#EAEAEA",
    borderRadius: dynamicSize(12),
    paddingHorizontal: dynamicSpacing(16),
    height: dynamicSize(56),
  },
  inputFocused: { borderColor: "#1A1A1A", backgroundColor: "#FFFFFF" },
  inputCurrency: {
    fontSize: dynamicFontSize(18),
    fontWeight: "600",
    color: "#1A1A1A",
    marginRight: dynamicSpacing(6),
  },
  fieldInput: {
    flex: 1,
    fontSize: dynamicFontSize(16),
    fontWeight: "600",
    color: "#1A1A1A",
    padding: 0,
  },

  /* ── Buttons ── */
  refreshBillBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: dynamicSize(1.5),
    borderColor: "#1A1A1A",
    backgroundColor: "transparent",
    paddingVertical: dynamicSpacing(14),
    borderRadius: dynamicSize(12),
    gap: dynamicSpacing(8),
    marginTop: dynamicSpacing(4),
  },
  refreshBillBtnText: {
    color: "#1A1A1A",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(14),
  },
  proceedBtn: {
    backgroundColor: Colours.PRIMARY,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: dynamicSpacing(18),
    borderRadius: dynamicSize(15),
    gap: dynamicSpacing(10),
  },
  disabledBtn: { backgroundColor: "#CCC" },
  proceedBtnText: {
    color: "white",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(16),
  },
  footerNote: {
    textAlign: "center",
    color: "#AAA",
    fontSize: dynamicFontSize(11),
    marginTop: dynamicSpacing(20),
  },

  /* ── Misc ── */
  loaderContainer: { paddingVertical: dynamicSpacing(20), alignItems: "center" },
  loadingText: { marginTop: dynamicSpacing(8), color: "#888", fontFamily: "Sen-Regular" },
  errorStateContainer: { paddingVertical: dynamicSpacing(20), alignItems: "center" },
  emptyText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Regular",
    color: "#999",
    textAlign: "center",
  },
});
